import React from "react";
import {
    ArrowUpward,
    CancelOutlined,
    CheckCircle,
    HelpOutlineOutlined,
} from "@mui/icons-material";
import type {Answer, Scenario, ScenarioExample} from "../showcaseData";
import {AgentBrand, AgentMark} from "./AgentBrand";

/**
 * One scenario, presented as two branded chats side by side — Microsoft 365
 * Copilot vs the Mithra agent — answering the same question. Reveals via `step`:
 *   0 — the question        1 — each assistant's answer     2 — audience prompt
 *   3 — root cause(s)        4 — the minimal worked example
 */
export const ScenarioStage: React.FC<{ scenario: Scenario; step: number }> = ({scenario, step}) => {
    const [thinking, setThinking] = React.useState(false);
    const [answered, setAnswered] = React.useState(false);

    // Step 1 — "ask the agents": a brief thinking state, then reveal both answers.
    React.useEffect(() => {
        if (step >= 1 && !answered) {
            setThinking(true);
            const t = setTimeout(() => {
                setThinking(false);
                setAnswered(true);
            }, 900);
            return () => clearTimeout(t);
        }
        if (step < 1 && answered) {
            setAnswered(false);
            setThinking(false);
        }
    }, [step, answered]);

    const showAsk = step >= 2;
    const reveal = step >= 3;
    const multiple = scenario.rootCauses.length > 1;

    return (
        <div className="dqs-stage">
            <header className="dqs-stage-head">
                <span className="dqs-stage-index">Scenario {scenario.index}</span>
            </header>

            <div className="dqs-flow">
                <div className="dqs-duo">
                    {scenario.answers.map((answer) => (
                        <ChatPanel
                            key={answer.agent}
                            answer={answer}
                            question={scenario.question}
                            thinking={thinking}
                            answered={answered}
                        />
                    ))}
                </div>

                {/* Audience prompt — the presenter pauses and asks the room */}
                <div className={`dqs-ask${showAsk ? ' is-shown' : ''}`}>
                    <HelpOutlineOutlined/>
                    <p>{scenario.prompt}</p>
                </div>

                {/* Root cause reveal — one merged card; the cause(s) apply to both answers */}
                <div className={`dqs-causes${reveal ? ' is-shown' : ''}`}>
                    <span className="dqs-causes-label">{multiple ? 'Root causes' : 'Root cause'}</span>
                    <div className="dqs-causes-card">
                        {scenario.rootCauses.map((c, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <span className="dqs-cause-plus">+</span>}
                                <span className="dqs-cause-pill">{c.label}</span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Worked example — revealed together with the root cause */}
                <div className={`dqs-example-wrap${reveal ? ' is-shown' : ''}`}>
                    <ExampleView example={scenario.example}/>
                </div>
            </div>
        </div>
    );
};

const ChatPanel: React.FC<{
    answer: Answer;
    question: string;
    thinking: boolean;
    answered: boolean;
}> = ({answer, question, thinking, answered}) => (
    // Both panels are Microsoft Copilot — identical styling; the right side is
    // distinguished only by its answer.
    <div className="dqs-chatpanel agent-copilot">
        <header className="dqs-panel-head">
            <AgentBrand agent={answer.agent}/>
        </header>

        <div className="dqs-chat">
            <div className="dqs-msg dqs-msg-user">
                <div className="dqs-bubble dqs-bubble-user">{question}</div>
            </div>

            {(thinking || answered) && (
                <div className="dqs-msg dqs-msg-agent">
                    <AgentMark agent={answer.agent} size={26}/>
                    <div className="dqs-bubble dqs-bubble-agent">
                        {thinking ? (
                            <span className="dqs-typing" aria-label="Assistant is responding">
                                <span/><span/><span/>
                            </span>
                        ) : (
                            <div className="dqs-fade-in dqs-answer">
                                <div className="dqs-answer-main">
                                    <span className="dqs-answer-value">{answer.value}</span>
                                </div>
                                <span className="dqs-answer-caption">{answer.caption}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

        <PanelInput/>
    </div>
);

/** A non-interactive Copilot input bar. */
const PanelInput: React.FC = () => (
    <div className="dqs-panel-foot">
        <div className="dqs-fauxinput">
            <span className="dqs-fauxinput-text">Message Copilot</span>
            <span className="dqs-fauxinput-send"><ArrowUpward sx={{fontSize: 16}}/></span>
        </div>
    </div>
);

/** Two data views side by side: Copilot on bad data vs Copilot on good (Mithra) data. */
const ExampleView: React.FC<{ example: ScenarioExample }> = ({example}) => (
    <div className="dqs-example">
        <div className="dqs-ex2">
            <ExampleSide good={false} example={example}/>
            <ExampleSide good={true} example={example}/>
        </div>
    </div>
);

/** Copilot logo + name + a Raw/Harmonised data tag — the side header. */
const ExampleHead: React.FC<{ good: boolean }> = ({good}) => (
    <span className="dqs-ex2-head">
        <AgentMark agent="copilot" size={18}/>
        Copilot
        <span className={`dqs-ex2-tag tone-${good ? 'good' : 'bad'}`}>{good ? 'Harmonised data' : 'Raw data'}</span>
    </span>
);

const Branch: React.FC<{ levels: string[]; leaf: 'ambiguous' | 'good' }> = ({levels, leaf}) => (
    <div className="dqs-ex-branch">
        {levels.map((lvl, j) => (
            <React.Fragment key={j}>
                {j > 0 && <span className="dqs-ex-sep">›</span>}
                <span className={`dqs-ex-chip${j === levels.length - 1 ? ` is-${leaf}` : ''}`}>{lvl}</span>
            </React.Fragment>
        ))}
    </div>
);

const ExampleSide: React.FC<{ good: boolean; example: ScenarioExample }> = ({good, example}) => {
    if (example.kind === 'overlap') {
        return (
            <div className={`dqs-ex2-side tone-${good ? 'good' : 'bad'}`}>
                <ExampleHead good={good}/>
                {good ? (
                    <>
                        <Branch levels={example.atlasBranch.levels} leaf="good"/>
                        <span className="dqs-ex2-foot">One home, classified by function</span>
                    </>
                ) : (
                    <>
                        {example.copilotBranches.map((b, i) => <Branch key={i} levels={b.levels} leaf="ambiguous"/>)}
                        <span className="dqs-ex2-foot">Two valid homes, picks one</span>
                    </>
                )}
            </div>
        );
    }

    const recognised = example.variants.filter(v => v.matched);
    const missed = example.variants.filter(v => !v.matched);

    return (
        <div className={`dqs-ex2-side tone-${good ? 'good' : 'bad'}`}>
            <ExampleHead good={good}/>
            {good ? (
                // Mithra grouped them: one box gathering every variant under the parent.
                <div className="dqs-ex-grouped">
                    <span className="dqs-ex-grouped-title">
                        <CheckCircle sx={{fontSize: 15}}/>
                        Grouped as {example.recogniseAs} by Mithra
                        <span className="dqs-ex-count">{example.variants.length}</span>
                    </span>
                    <div className="dqs-ex-chips">
                        {example.variants.map((v, i) => <span className="dqs-ex-row" key={i}>{v.text}</span>)}
                    </div>
                </div>
            ) : (
                // Copilot did NOT group them — plain labelled lists, no box.
                <div className="dqs-ex-flat">
                    <ExampleSection
                        title={`Recognised as ${example.recogniseAs}`}
                        tone="ok" items={recognised.map(v => v.text)}/>
                    <ExampleSection
                        title={`Not recognised as ${example.recogniseAs}`}
                        tone="miss" items={missed.map(v => v.text)}/>
                </div>
            )}
        </div>
    );
};

const ExampleSection: React.FC<{ title: string; tone: 'ok' | 'miss'; items: string[] }> = ({title, tone, items}) => (
    <div className={`dqs-ex-section tone-${tone}`}>
        <span className="dqs-ex-section-title">
            {tone === 'ok' ? <CheckCircle sx={{fontSize: 14}}/> : <CancelOutlined sx={{fontSize: 14}}/>}
            {title}
            <span className="dqs-ex-count">{items.length}</span>
        </span>
        <div className="dqs-ex-chips">
            {items.map((t, i) => <span className="dqs-ex-row" key={i}>{t}</span>)}
        </div>
    </div>
);
